/* ============================================================================
   PrioritiesSyncer - bridges AuthProvider and SessionProvider for priorities.
   ----------------------------------------------------------------------------
   This component sits inside both AuthProvider and SessionProvider in the
   component tree. It uses useAuth() to detect sign-in state and authedFetch,
   and uses useSession() to read/write userPriorities.

   Responsibilities:
     - On sign-in: GET /api/priorities and merge into session state.
     - On sign-out: reset session priorities to defaults.
     - When priorities change (via setUserPriorities): PUT /api/priorities
       in the background.

   Implemented as a bridge because SessionProvider is the outer provider and
   cannot call useAuth() directly (AuthProvider is nested inside it).
============================================================================ */
import { useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { DEFAULT_USER_PRIORITIES, type UserPriorities } from "@/lib/userPriorities";
import { sanitizePriorityFriendIds } from "@/lib/priorityFriends";

export function PrioritiesSyncer() {
  const { isSignedIn, authedFetch, account } = useAuth();
  const { userPriorities, setUserPriorities, resetUserPriorities, tier, setTier } = useSession();

  // ── Tier sync ──────────────────────────────────────────────────────────────
  // Mirror the authoritative `account.tier` from the server into the
  // SessionProvider tier state which drives all gating UI. Without this
  // bridge a paying Pro user appears as Basic on every screen.
  // Special-case: leave "trial" alone so the dev paywall states still work,
  // and never overwrite a stronger client-only state.
  useEffect(() => {
    if (!isSignedIn) {
      if (tier !== "free") setTier("free");
      return;
    }
    const serverTier = account?.tier;
    if (serverTier === "pro" && tier !== "pro") setTier("pro");
    if (serverTier === "free" && tier !== "free" && tier !== "trial") {
      setTier("free");
    }
  // setTier is stable; tier is intentionally a dep so we re-evaluate after
  // local tier changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, account?.tier]);

  // Track previous sign-in state to detect transitions.
  const prevSignedInRef = useRef(isSignedIn);
  // Track the last priorities object we saved to the server so we can skip
  // redundant saves (e.g. the initial server-load itself triggering a PUT).
  const lastSavedRef = useRef<UserPriorities | null>(null);

  // Helper: fire-and-forget PUT /api/priorities. Swallows all errors so
  // background saves never crash the UI. 401 is silently ignored (signed out).
  const savePrioritiesToServer = (next: UserPriorities) => {
    if (!isSignedIn) return;
    authedFetch("/api/priorities", {
      method: "PUT",
      body: JSON.stringify({
        priorities: next,
        questionnaireCompletedAt: next.questionnaireCompletedAt ?? null,
      }),
    }).catch((err) => {
      console.error("[priorities] background save failed", err);
    });
  };

  // Sign-in / sign-out effect: load from server on sign-in, clear on sign-out.
  useEffect(() => {
    const wasSignedIn = prevSignedInRef.current;
    prevSignedInRef.current = isSignedIn;

    // Just signed out: reset to defaults.
    if (!isSignedIn && wasSignedIn) {
      lastSavedRef.current = null;
      resetUserPriorities();
      return;
    }

    if (!isSignedIn) return;

    // Just signed in (or initial mount while signed in): fetch saved priorities.
    authedFetch("/api/priorities")
      .then(async (res) => {
        if (!res.ok) return;
        const data = await res.json() as {
          priorities: UserPriorities | null;
          questionnaireCompletedAt: string | null;
          updatedAt: string | null;
        };
        if (data.priorities) {
          // Pinned friends (Friends > Manage) ride in this blob and
          // are absent on any account that predates the feature, so normalise
          // the key here — every downstream read can then assume an array.
          const hydrated: UserPriorities = {
            ...data.priorities,
            priorityFriendIds: sanitizePriorityFriendIds(data.priorities.priorityFriendIds),
          };
          // Mark as loaded so the write effect below doesn't immediately
          // re-save what we just loaded from the server.
          lastSavedRef.current = hydrated;
          setUserPriorities(hydrated);
        }
      })
      .catch((err) => {
        console.error("[priorities] load from server failed", err);
      });
  // authedFetch and resetUserPriorities are stable references.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  // Write effect: whenever priorities change while signed in, persist to server.
  // Skip the save if this is the value we just loaded from the server.
  useEffect(() => {
    if (!isSignedIn) return;
    if (lastSavedRef.current === userPriorities) return;
    // Skip default priorities (no point persisting the startup placeholder).
    if (userPriorities === DEFAULT_USER_PRIORITIES) return;
    lastSavedRef.current = userPriorities;
    savePrioritiesToServer(userPriorities);
  // savePrioritiesToServer is defined in this render scope - it captures
  // the current isSignedIn / authedFetch correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPriorities, isSignedIn]);

  // Renders nothing - pure side-effect component.
  return null;
}
